import axios from 'axios'

import type { ContestGame, Survey } from '@/types/types'

export type MissionInstance = {
  id: number
  label: string
}

type InstanceState = {
  items: Ref<MissionInstance[]>
  isLoaded: Ref<boolean>
  hasError: Ref<boolean>
}

const CONTEST_GAMES_PAGE_SIZE = 50
const CONTEST_GAMES_MAX_PAGES = 10
const SURVEYS_PAGE_SIZE = 250

const surveys: InstanceState = { items: ref([]), isLoaded: ref(false), hasError: ref(false) }
const contestGames: InstanceState = { items: ref([]), isLoaded: ref(false), hasError: ref(false) }
const isLoading = ref(false)

const isLaunched = (launchDate?: number | null) =>
  launchDate === null || launchDate === undefined || launchDate * 1000 <= Date.now()

const stateFor = (type: string | null): InstanceState | null => {
  if (type === 'survey') return surveys
  if (type === 'contest_game') return contestGames

  return null
}

const loadSurveys = async (): Promise<MissionInstance[]> => {
  const { data } = await axios.get<{ data: Survey[] }>('/surveys', {
    params: { per_page: SURVEYS_PAGE_SIZE, page: 1 }
  })

  return (data.data ?? [])
    .filter((survey) => survey.isActive && !survey.isTerminated && isLaunched(survey.launchDate))
    .map((survey) => ({ id: survey.id, label: survey.questionText }))
}

const loadContestGames = async (): Promise<MissionInstance[]> => {
  const collected: MissionInstance[] = []

  for (let page = 1; page <= CONTEST_GAMES_MAX_PAGES; page++) {
    const { data } = await axios.get<{ data: ContestGame[] }>('/contest-games', {
      params: { per_page: CONTEST_GAMES_PAGE_SIZE, page }
    })
    const batch = data.data ?? []

    collected.push(
      ...batch
        .filter((contestGame) => contestGame.isVisibleInWebapp)
        .map((contestGame) => ({ id: Number(contestGame.id), label: contestGame.name }))
    )

    if (batch.length < CONTEST_GAMES_PAGE_SIZE) break
  }

  return collected
}

const load = async (state: InstanceState, loader: () => Promise<MissionInstance[]>) => {
  state.hasError.value = false

  try {
    state.items.value = await loader()
    state.isLoaded.value = true
  } catch {
    state.items.value = []
    state.hasError.value = true
  }
}

export default function useMissionInstances() {
  const instancesFor = (type: string | null): MissionInstance[] => stateFor(type)?.items.value ?? []

  const errorFor = (type: string | null): boolean => stateFor(type)?.hasError.value ?? false

  const needsFetch = (type: string | null): boolean => {
    const state = stateFor(type)

    return state !== null && (!state.isLoaded.value || state.hasError.value || state.items.value.length === 0)
  }

  const fetchInstances = async (refresh: boolean | string | null = false) => {
    if (isLoading.value) return

    const forced = refresh === true ? [surveys, contestGames] : [stateFor(typeof refresh === 'string' ? refresh : null)]
    const pending = [surveys, contestGames].filter((state) => forced.includes(state) || !state.isLoaded.value)

    if (pending.length === 0) return

    isLoading.value = true

    try {
      await Promise.all(pending.map((state) => load(state, state === surveys ? loadSurveys : loadContestGames)))
    } finally {
      isLoading.value = false
    }
  }

  return { isLoading, fetchInstances, instancesFor, errorFor, needsFetch }
}
